import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import { getClip, type Clip } from '../api/clips';
import { ApiError } from '../api/client';
import { useHighlightDraft } from '../stores/highlightDraft';

export function isClipGoneError(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 404 || err.code === 'not_found');
}

export async function loadStagedClips(ids: string[]): Promise<{
  clips: Array<Clip | null>;
  gone: string[];
}> {
  const gone: string[] = [];
  const clips = await Promise.all(ids.map(async (id) => {
    try {
      return await getClip(id);
    } catch (err) {
      if (isClipGoneError(err)) {
        gone.push(id);
        return null;
      }
      return null;
    }
  }));
  return { clips, gone };
}

/** Timeline IDs live in localStorage. Drop any the clipper deleted or unpublished. */
export function useStagedClips() {
  const draft = useHighlightDraft();
  const query = useQuery({
    queryKey: ['clips', 'staged', draft.clipIds.join(',')],
    queryFn: () => loadStagedClips(draft.clipIds),
    enabled: draft.clipIds.length > 0,
    refetchInterval: draft.clipIds.length > 0 ? 8_000 : false,
    retry: false,
  });

  useEffect(() => {
    const gone = query.data?.gone;
    if (gone && gone.length > 0) draft.pruneGone(gone);
  }, [query.data, draft]);

  return {
    clips: query.data?.clips ?? [],
    isLoading: query.isLoading,
  };
}
